import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { apiGet, apiPostJson, ApiError } from '../api/client';

export interface User {
  id: number;
  username: string;
  role: string;
}

export const ROLES = {
  OWNER: 'Owner',
  STARBAR: 'Starbar',
} as const;

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // The session lives in an httpOnly cookie, so the app cannot read it
  // directly — it asks the server who it is on load.
  useEffect(() => {
    let cancelled = false;
    apiGet<{ user: User }>('/auth/me')
      .then((res) => {
        if (!cancelled) setUser(res.user);
      })
      .catch((err) => {
        // 401 just means "not logged in", which is not an error worth surfacing.
        if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async login(username, password) {
        const res = await apiPostJson<{ user: User }>('/auth/login', { username, password });
        setUser(res.user);
      },
      async logout() {
        await apiPostJson('/auth/logout', {});
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
